import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik-2';
import { Translate, withLocalize } from 'react-localize-redux';
import { useQuery } from '@apollo/client';
import { sortAutoCompleteOptions } from '../../../utils/components/formUtils';
import css from './AppAutocomplete.css';
import { partnerShape, siteShape, templateShape } from '../types';
import { AutoCompleteField } from '../../../components/widgets';
import { GET_PARTNER_CONFIGURATIONS_FOR_PARTNER } from '../../queries';
import { APP_AUTOCOMPLETE_PLACEHOLDER_MESSAGES } from '../../consts';
import { AUTOCOMPLETE_LAYOUTS } from '../../../components/molecules/AutoCompleteField';

const DEFAULT_APP_ICON = 'https://cdn.singular.net/dashboard/icon/1dbacc8dc07551adf8fffa21d4dee93a79f37105';

const AppAutocomplete = ({
    label = '',
    disabled = false,
    partner,
    sites = [],
    postbackTemplates = [],
    translate = () => {},
    onChange = () => {},
    labelClassName = '',
    layout = AUTOCOMPLETE_LAYOUTS.VERTICAL,
    selectedAgencyId = null,
    showEmptyState = true,
}) => {
    const { values, initialValues, touched, errors } = useFormikContext();
    const partnerConfigurationsResponse = useQuery(GET_PARTNER_CONFIGURATIONS_FOR_PARTNER, {
        variables: { partnerId: partner.id },
    });

    const platformsWithTemplateSet = new Set(postbackTemplates.map(template => template.platform));
    const { partnerConfigurations = [] } = partnerConfigurationsResponse.data || {};
    // Filter configurations based on selected agency:
    // - If no agency selected: check for configurations without agency (agencyId is null/undefined)
    // - If agency selected: check for configurations with that specific agency
    const relevantConfigurations = partnerConfigurations.filter(({ agencyId }) => {
        return selectedAgencyId ? agencyId === selectedAgencyId : !agencyId;
    });
    const configuredSites = relevantConfigurations.map(config => config.siteId);

    const getSiteOption = site => {
        const isAlreadyConfigured = configuredSites.includes(site.site_id);
        const isPlatformUnsupported = !platformsWithTemplateSet.has(site.platform);

        let extra = null;
        if (isAlreadyConfigured) {
            extra = translate('STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.CONFIGURATION_ALREADY_EXISTS');
        } else if (isPlatformUnsupported) {
            extra = translate('STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PLATFORM_NOT_SUPPORTED');
        }

        return {
            value: site.site_id,
            label: site.fullName,
            icon: <img className={css.siteOptionIcon} src={site.site_icon || DEFAULT_APP_ICON} />,
            isDisabled: isAlreadyConfigured || isPlatformUnsupported,
            extra,
        };
    };

    const sitesOptions = sites.map(getSiteOption).sort(sortAutoCompleteOptions);

    const selectedSite = values.site || initialValues.site;
    const selectedSiteOption = sitesOptions.find(({ value }) => value === selectedSite);

    if (!selectedSiteOption && disabled) {
        return showEmptyState ? (
            <div className={css.siteNotFound}>
                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.APP_NOT_FOUND" />
            </div>
        ) : null;
    }

    const placeholder =
        sites.length === 0
            ? APP_AUTOCOMPLETE_PLACEHOLDER_MESSAGES.noIntegratedApps
            : APP_AUTOCOMPLETE_PLACEHOLDER_MESSAGES.selectApp;

    return (
        <AutoCompleteField
            label={label}
            disabled={disabled}
            searchable
            isMulti={false}
            options={sitesOptions}
            value={selectedSiteOption}
            placeholder={placeholder}
            containerClass={css.siteField}
            error={touched.site && errors.site}
            onChange={onChange}
            selectOptions={{ usePortal: true }}
            labelClassName={labelClassName}
            layout={layout}
        />
    );
};

AppAutocomplete.propTypes = {
    label: PropTypes.string,
    disabled: PropTypes.bool,
    partner: partnerShape.isRequired,
    sites: PropTypes.arrayOf(siteShape),
    postbackTemplates: PropTypes.arrayOf(templateShape),
    translate: PropTypes.func,
    onChange: PropTypes.func,
    labelClassName: PropTypes.string,
    layout: PropTypes.oneOf(Object.values(AUTOCOMPLETE_LAYOUTS)),
    selectedAgencyId: PropTypes.string,
    showEmptyState: PropTypes.bool,
};

export default withLocalize(AppAutocomplete);
